import * as RadixTooltip from '@radix-ui/react-tooltip'
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Tooltip.module.scss'

export const TooltipProvider = RadixTooltip.Provider

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  side?: RadixTooltip.TooltipContentProps['side']
  align?: RadixTooltip.TooltipContentProps['align']
  delayDuration?: number
  /** Controle externo — ex.: abrir por toque, que o Radix ignora. */
  open?: boolean | undefined
  onOpenChange?: ((open: boolean) => void) | undefined
}

/**
 * Simple tooltip — wrap any trigger with `<Tooltip content="..."><Button>Go</Button></Tooltip>`.
 * Requires `<TooltipProvider>` somewhere above in the tree.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 200,
  open,
  onOpenChange,
}: TooltipProps) {
  const controlled = open === undefined ? {} : { open, ...(onOpenChange ? { onOpenChange } : {}) }
  return (
    <RadixTooltip.Root delayDuration={delayDuration} {...controlled}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <TooltipContent side={side} align={align}>{content}</TooltipContent>
    </RadixTooltip.Root>
  )
}

const TooltipContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixTooltip.Content>
>(function TooltipContent({ className, sideOffset = 6, children, ...rest }, ref) {
  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(styles.content, className)}
        {...rest}
      >
        {children}
        <RadixTooltip.Arrow className={styles.arrow} />
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  )
})
